import React, { CSSProperties, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ResponsiveFlex } from 'components/common/styled/common-styled';
import { CollectionTitle } from 'features/Reserve/components';
import moment from 'moment';
import { Route, useRouteMatch } from 'react-router-dom';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import useAvailableDates from 'features/Reserve/hooks/useAvailableDates';
import { DATE_FORMAT } from 'utils';
import { tabletBreakpoint, useIsMobile } from 'WindowDimensionProvider';
import { NonMappedResourceGroupPage } from 'features/Reserve/pages/ResourceGroupPage/NonMappedResourceGroupPage';
import { AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import NonMappedResourceGroupCardWrapper from 'features/Reserve/components/NonMappedResourceGroupCardWrapper';
import DatePickerButton from 'components/DatePickerButton';
import useBrandById from 'api/brand/useBrandById';
import { HyattExperienceBenefits } from 'components/molecules/HyattExperienceBenefits';
import useGetEligibleProgram from 'api/brand/useGetEligibleProgram';
import isHyattEligible from 'utils/pointEligibility/isHyattEligible';
import { ListingSeo } from 'components/organisms/ListingSeo';

export const NonMappedCollectionPage = () => {
  const isMobileView = useIsMobile();
  const { t: translate } = useTranslation();
  const match = useRouteMatch();
  const {
    listing,
    latest,
    isLoadingSessions,
    cancellationPolicy,
    firstAvailableDate,
    isLoading,
  } = useResourceBookingSession();

  const promoterBrand = useBrandById(listing?.providerBrandId ?? null);
  const { data: eligibleProgram } = useGetEligibleProgram(
    listing?.brandId,
    listing?.id && listing?.brandId && listing?.productLine
      ? {
          listings: [
            {
              id: listing.id,
              brandId: listing.brandId,
              productLine: listing.productLine,
            },
          ],
        }
      : null,
  );

  const [showDatePicker, setShowDatePicker] = useState(false);

  const [datePickerDate, setDatePickerDate] = useState<moment.Moment | null>(
    null,
  );
  const datePickerDateObject = datePickerDate?.toDate();
  const initialVisibleMonth = useMemo(
    () =>
      !isLoading && !!firstAvailableDate?.firstAvailableDate
        ? moment(firstAvailableDate?.firstAvailableDate)
        : moment(new Date()),

    [firstAvailableDate, isLoading],
  );

  const [dateForCalendarAvailability, setDateForCalendarAvailability] =
    useState<moment.Moment | null>(initialVisibleMonth);

  const { availableDates, isLoadingAvailableDates } = useAvailableDates({
    from: dateForCalendarAvailability
      ?.clone()
      .startOf('month')
      .format(DATE_FORMAT),
    to: dateForCalendarAvailability?.clone().endOf('month').format(DATE_FORMAT),
    // Availability is keyed on the real collection UUID; the route value may be a slug.
    collectionId: listing?.resourceGroupCollectionId,
    latest,
    skip: !showDatePicker || !firstAvailableDate,
  });

  const vibesEnabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.ENABLE_VIBES_V2,
  );

  const handleDateChange = (newDate: moment.Moment | null) => {
    setDatePickerDate(newDate);
  };

  if (!listing) {
    return <h1>{translate('noCollectionFound')}</h1>;
  }

  const vibes = listing.vibes.map(({ name }) => name);

  const handleIsDayBlocked = (day: moment.Moment) => {
    const formattedDay = day.format('YYYY-MM-DD');
    return !availableDates.some((date) => date === formattedDay);
  };

  const handleMonthChange = (newDate: moment.Moment) => {
    setDateForCalendarAvailability(newDate);
  };

  const handleClearButtonClick = () => {
    setDatePickerDate(null);
  };

  return (
    <CollectionDetailsContainer gap="36px">
      <ListingSeo listing={listing} />
      <ResponsiveFlex
        breakpoint={tabletBreakpoint}
        flex={1}
        justifyContent="space-between"
        mobileOverride={{
          direction: 'column',
          alignItem: 'flex-start',
          justifyContent: 'stretch',
          gap: 12,
        }}
      >
        <div>
          <CollectionTitle
            allowMultipleLines
            className="non-mapped-collection-title"
          >
            {listing.title}
          </CollectionTitle>
          {promoterBrand?.data?.name && (
            <BrandName>
              {translate('by')} {promoterBrand.data.name}
            </BrandName>
          )}
        </div>
        <DatePickerButton
          className="non-mapped-date-picker"
          date={datePickerDate}
          initialVisibleMonth={dateForCalendarAvailability}
          isDayBlocked={handleIsDayBlocked}
          isLoadingDates={isLoadingAvailableDates}
          onClearButtonClick={handleClearButtonClick}
          onDateChange={handleDateChange}
          onNextMonthClick={handleMonthChange}
          onOpenChange={setShowDatePicker}
          onPrevMonthClick={handleMonthChange}
          open={showDatePicker}
          showClearButton
        />
      </ResponsiveFlex>
      {isHyattEligible(eligibleProgram) && (
        <HyattExperienceBenefits
          payEligible={isHyattEligible(eligibleProgram)?.payEligible}
          earnEligible={isHyattEligible(eligibleProgram)?.earnEligible}
        />
      )}
      <ResponsiveFlex
        direction="column"
        alignItem="flex-start"
        justifyContent="flex-start"
        width="100%"
        gap={36}
      >
        {listing.resourceGroups
          ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((group) => (
            <NonMappedResourceGroupCardWrapper
              cancellationPolicy={cancellationPolicy}
              listing={listing}
              group={group}
              isLoading={isLoadingSessions}
              key={group.id}
              selectedDate={datePickerDateObject}
              vibes={vibesEnabled ? vibes : undefined}
              promoterBrand={promoterBrand?.data}
              firstAvailableDate={firstAvailableDate}
              isLoadingFirstAvailableDate={isLoading}
            />
          ))}
      </ResponsiveFlex>
      {isMobileView && (
        <Route
          exact
          path={`${match.path}/group/:groupId`}
          component={NonMappedResourceGroupPage}
        />
      )}
    </CollectionDetailsContainer>
  );
};

const CollectionDetailsContainer = styled.div<{ gap?: CSSProperties['gap'] }>`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  gap: ${(props) => props.gap || '24px'};
`;

export const BrandName = styled.p`
  color: var(--Text-Tertiary, rgba(0, 0, 0, 0.6));
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.016px;
  margin-top: 8px;
  margin-bottom: 0px;
`;
